import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { Button, Flex } from 'antd';

import dayjs from 'dayjs';

import axios from '../../../../axios';

import { sortDates, sortNumbers, sortStrings } from '../../../../helpers/filter';
import { hasPermission } from '../../../../helpers/user';

import { DATE_FORMAT } from '../../../../constants/common';
import messages from '../../../../constants/messages';
import { PERMISSIONS } from '../../../../constants/permissions';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../../constants/table-configurations';
import { GATEWAY_CSV_TEMPLATE } from '../../../../constants/templates';
import { GATEWAYS_CSV_VALIDATION_SCHEMA } from '../../../../constants/validation-schemas';

import { useModal } from '../../../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../../../hooks/useUnsavedChangesPrompt';

import { createNewGateway, getAllGateways, searchGateways } from '../../../../redux/actions/gateway';

import AppModal from '../../../../components/AppModal';
import AppTable from '../../../../components/AppTable';
import { COLUMN_SIZE } from '../../../../components/AppTable/constants';
import UploadModal from '../../../../components/Modals/UploadModal';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../../components/action_notifier';
import UnsavedChangesModal from '../../../../components/ui/UnsavedChangesModal';

import GatewayNewModal from './GatewayNewModal';

const commonColumnProperties = {
  ellipsis: true,
  searchable: true,
  sortDirections: ['ascend', 'descend']
};

function GatewaysTable() {
  const dispatch = useDispatch();
  const history = useHistory();

  const formRef = useRef(null);
  const formCreateRef = useRef(null);

  const { gatewaysAll, gatewaysLoading } = useSelector(state => state.gateway);
  const { permissions } = useSelector(state => state.user);

  const [createFormSubmittable, setCreateFormSubmittable] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [selection, setSelection] = useState({ selectAll: false, selectedGateways: [] });
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    add_modal: false,
    delete_modal: false,
    query: '',
    uploadSuccessMessage: ''
  });

  const [uploadModalVisible, openUploadModal, closeUploadModal] = useModal();
  const [uploadSuccessModalVisible, openUploadSuccessModal, closeUploadSuccessModal] = useModal();

  useEffect(() => {
    dispatch(getAllGateways());
  }, []);

  const selectRecords = useCallback(
    recordIds => {
      setSelection({ selectedGateways: recordIds });
    },
    [state.selectedGateways]
  );

  const toggleModal = modal => {
    formCreateRef.current?.resetFields();
    setState(state => ({ ...state, [modal]: !state[modal] }));
  };

  const actions = initActions();

  function initActions() {
    const isAnyGatewaySelected = selection.selectedGateways?.length;
    return [
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={
              isAnyGatewaySelected ||
              selection.selectedGateways?.length ||
              !hasPermission([PERMISSIONS.GATEWAYS.CREATE], permissions)
            }
            onClick={() => toggleModal('add_modal')}>
            Add Kraal gateway
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyGatewaySelected || !hasPermission([PERMISSIONS.GATEWAYS.DELETE_ALL], permissions)}
            onClick={() => toggleModal('delete_modal')}>
            Archive Kraal gateway
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!hasPermission([PERMISSIONS.GATEWAYS.CREATE], permissions)}
            onClick={openUploadModal}>
            Upload Kraal gateway list
          </Button>
        )
      }
    ];
  }

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Gateway ID',
      dataIndex: 'identifier',
      fixed: 'left',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier),
      render: (value, record) => <Link to={`gateway/${record.id}`}>{record.identifier}</Link>
    },
    {
      ...commonColumnProperties,
      title: 'Gateway Name',
      dataIndex: 'name',
      configColumnKey: TABLE_CONFIG_COLUMNS.NAME,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.name, b.name),
      render: (value, record) => <Link to={`gateway/${record.id}`}>{value}</Link>
    },
    {
      ...commonColumnProperties,
      title: 'Appeui',
      dataIndex: 'appeui',
      configColumnKey: TABLE_CONFIG_COLUMNS.APPEUI,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.appeui, b.appeui)
    },
    {
      ...commonColumnProperties,
      title: 'Gweui',
      dataIndex: 'gweui',
      configColumnKey: TABLE_CONFIG_COLUMNS.GWEUI,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.gweui, b.gweui)
    },
    {
      ...commonColumnProperties,
      title: 'Serial Number',
      dataIndex: 'serial_number',
      configColumnKey: TABLE_CONFIG_COLUMNS.NUMBER,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortNumbers(a.serial_number, b.serial_number)
    },
    {
      ...commonColumnProperties,
      title: 'ARN',
      dataIndex: 'arn',
      configColumnKey: TABLE_CONFIG_COLUMNS.ARN,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.arn, b.arn)
    },
    {
      ...commonColumnProperties,
      title: 'Version',
      dataIndex: 'version',
      configColumnKey: TABLE_CONFIG_COLUMNS.VERSION,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.version, b.version)
    },
    {
      ...commonColumnProperties,
      title: 'Carrier Type',
      dataIndex: 'carrier_type',
      configColumnKey: TABLE_CONFIG_COLUMNS.TYPE,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.carrier_type, b.carrier_type)
    },
    {
      ...commonColumnProperties,
      title: 'Connection Speed',
      dataIndex: 'connection_speed',
      configColumnKey: TABLE_CONFIG_COLUMNS.CONNECTION_SPEED,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortNumbers(a.connection_speed, b.connection_speed)
    },
    {
      ...commonColumnProperties,
      title: 'Lora Signal Strength',
      dataIndex: 'lora_signal_strength',
      configColumnKey: TABLE_CONFIG_COLUMNS.LORA_SIGNAL_STRENGTH,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortNumbers(a.lora_signal_strength, b.lora_signal_strength)
    },
    {
      ...commonColumnProperties,
      title: 'Lat',
      dataIndex: 'lat',
      configColumnKey: TABLE_CONFIG_COLUMNS.LAT,
      width: COLUMN_SIZE.SM,
      sorter: (a, b) => sortNumbers(a.lat, b.lat)
    },
    {
      ...commonColumnProperties,
      title: 'Lng',
      dataIndex: 'lng',
      configColumnKey: TABLE_CONFIG_COLUMNS.LNG,
      width: COLUMN_SIZE.SM,
      sorter: (a, b) => sortNumbers(a.lng, b.lng)
    },
    {
      ...commonColumnProperties,
      title: 'Creation Date',
      dataIndex: 'created_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.CREATED_AT,
      width: COLUMN_SIZE.XL,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATETIME) : null),
      sorter: (a, b) => sortDates(a.created_at, b.created_at)
    },
    {
      ...commonColumnProperties,
      title: 'Update Date',
      dataIndex: 'updated_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.UPDATED_AT,
      width: COLUMN_SIZE.XL,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATETIME) : null),
      sorter: (a, b) => sortDates(a.updated_at, b.updated_at)
    }
  ];

  function getGatewaysBySearchQuery(searchQuery) {
    dispatch(searchGateways({ query: searchQuery }));
  }

  // const handleCSVChange = event => {
  //   const reader = new FileReader();
  //
  //   reader.readAsText(event.target.files[0]);
  //   reader.onload = function () {
  //     setState({ csvfile: reader.result, uploading: false });
  //   };
  //
  //   reader.onerror = function (error) {
  //     console.error('Error: ', error);
  //   };
  // };

  const addNewGateway = async data => {
    await dispatch(createNewGateway(data));
    toggleModal('add_modal');
    successToastHandler(messages.CREATED);
  };

  const deleteGateways = async () => {
    const response = await axios.delete('gateways/archive', {
      data: {
        ids: selection.selectedGateways
      }
    });

    setSelection(prevState => ({ ...prevState, selectAll: false, selectedGateways: [] }));

    if (response.status === 200) {
      dispatch(getAllGateways());
      toggleModal('delete_modal');
    }
  };

  const uploadCSV = async csv => {
    try {
      const response = await axios.post('gateways/store/csv', { csv });

      if (response.status === 200) {
        setState(prevState => ({ ...prevState, uploadSuccessMessage: response?.data?.message }));
        await dispatch(getAllGateways()).unwrap();
        closeUploadModal();
        openUploadSuccessModal();
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  };

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (createFormSubmittable) {
          await addNewGateway();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formCreateRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        setIsFormDirty(false);
        toggleModal('add_modal');
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : toggleModal('add_modal');
  }, [isFormDirty]);

  return (
    <>
      {NavigationPrompt}
      <AppTable
        headerClass="py-2"
        searchPlaceholder="Search Kraal Gateways"
        actions={actions}
        baseColumns={columns}
        dataSource={gatewaysAll}
        filterable={false}
        loading={gatewaysLoading}
        searchable={true}
        tableConfigKey={TABLE_CONFIG_KEYS.ADMIN_GATEWAYS}
        handleOnSelectRecords={selectRecords}
        handleSearchChange={getGatewaysBySearchQuery}
      />

      <GatewayNewModal
        formRef={formCreateRef}
        isOpen={state.add_modal}
        submittable={createFormSubmittable}
        onCancel={handleCancel}
        onChange={setIsFormDirty}
        onSubmit={addNewGateway}
        setCreateFormSubmittable={setCreateFormSubmittable}
      />

      <AppModal
        isOpen={state.delete_modal}
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive gateways"
        handleCancel={() => toggleModal('delete_modal')}
        handleConfirm={deleteGateways}>
        <div className="py-4">
          {selection.selectedGateways.some(id => gatewaysAll.find(g => g.id === id)?.connected_tags_count)
            ? 'This gateway has active tag connections. Archiving this gateway will disconnect all tags connected to this gateway. Are you sure you want to archive this gateway?'
            : messages.ARCHIVE_CONFIRMATION('selected gateways')}
        </div>
      </AppModal>

      <UploadModal
        fileName="gateways-template.csv"
        formRef={formRef}
        isOpen={uploadModalVisible}
        schema={GATEWAYS_CSV_VALIDATION_SCHEMA}
        submittable={submittable}
        template={GATEWAY_CSV_TEMPLATE}
        onCancel={closeUploadModal}
        onUpload={uploadCSV}
        setSubmittable={setSubmittable}
      />

      <AppModal
        confirmButtonText="Ok"
        cancelButtonHidden={true}
        isOpen={uploadSuccessModalVisible}
        handleConfirm={closeUploadSuccessModal}>
        <Flex align="center" justify="center" className="pb-2 pt-5">
          {state.uploadSuccessMessage}
        </Flex>
      </AppModal>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}

export default memo(GatewaysTable);
